import api from '../lib/api';

export interface Survey {
  name: string;
  description: string;
  questions: SurveyQuestion[];
}

export interface SurveyQuestion {
  questionType: string;
  order: number;
  isRequired: boolean;
  answerOptions: { mainQuestion: string; translationKey: string; additionalInfo?: string }[];
  mainText: string;
  helpText?: string | null;
  section?: string;
}

export interface GetSurveyQuestionsParams {
  surveyId: string;
}

// Backend API Survey Question format (raw from API)
export interface ApiSurveyQuestion {
  id: string;
  analysisKeyId: string;
  analysisKey: string;
  dataType: 'select' | 'selectMultiple' | 'number' | 'text';
  widgetType: string;
  required: boolean;
  answerOptions: string[];
  projectId: string;
  sharedAt: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export const getSurveyQuestions = async ({
  surveyId,
}: GetSurveyQuestionsParams): Promise<ApiSurveyQuestion[]> => {
  const questions = await api.apiRequest({
    endpoint: `/api/surveys/${surveyId}/questions`,
    service: 'survey',
    method: 'GET',
  });

  return questions as ApiSurveyQuestion[];
};

// Survey response types and functions
export interface CreateSurveyResponseParams {
  surveyId: string;
  respondentType: string;
}

export interface SurveyResponse {
  id: string;
  respondentType: string;
  surveyId: string;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface CreateSurveyAnswerParams {
  value: string[];
  responseId: string;
  questionId: string;
  analysisKeyId: string;
}

export const createSurveyResponse = async ({
  surveyId,
  respondentType = 'user',
}: CreateSurveyResponseParams): Promise<SurveyResponse> => {
  const response = await api.apiRequest({
    endpoint: '/api/responses',
    service: 'survey',
    method: 'POST',
    body: { surveyId, respondentType },
  });

  return response as SurveyResponse;
};

export const submitSurveyAnswer = async (answer: CreateSurveyAnswerParams): Promise<void> => {
  await api.apiRequest({
    endpoint: '/api/answers',
    service: 'survey',
    method: 'POST',
    body: {
      value: answer.value,
      responseId: answer.responseId,
      questionId: answer.questionId,
      analysisKeyId: answer.analysisKeyId,
    },
  });
};

export const completeSurveyResponse = async (responseId: string): Promise<SurveyResponse> => {
  const response = await api.apiRequest({
    endpoint: `/api/responses/${responseId}`,
    service: 'survey',
    method: 'PUT',
    body: { completedAt: new Date().toISOString() },
  });

  return response as SurveyResponse;
};
